import React, { useState, useEffect, useMemo } from 'react';
import SortableTable from './SortableTable';
import { SENSOR_TYPES, DRAWING_MODES, SENSOR_COLORS, SPACE_TYPES, SPACE_TYPE_COLORS } from '../utils/constants';
import { formatArea, METERS_TO_FEET } from '../utils/conversion';
import DeleteSpaceButton from './DeleteSpaceButton';
import InPlaceNumberEditor from './InPlaceNumberEditor';
import './SpaceTable.css';

/**
 * SpaceTable component for displaying spaces in a sortable table
 * 
 * @param {Object} props - Component props
 * @param {Array} props.boxes - Array of box objects
 * @param {Array} props.shapes - Array of shape objects
 * @param {Array} props.entryPoints - Array of entry point objects
 * @param {Object} props.selectedSpace - Currently selected space
 * @param {Array} props.selectedSpaces - Array of selected spaces for multi-selection
 * @param {boolean} props.isMultiSelecting - Whether multi-selection is active
 * @param {string} props.activeMode - Current active drawing mode
 * @param {Function} props.setSelectedSpace - Function to set selected space
 * @param {Function} props.setSelectedSpaces - Function to set selected spaces for multi-selection
 * @param {Function} props.setIsMultiSelecting - Function to set multi-selection mode
 * @param {Function} props.toggleSpaceType - Function to toggle space type
 * @param {Function} props.updateSpaceCount - Function to update space count
 * @param {Function} props.toggleSpaceVisibility - Function to toggle space visibility
 * @param {Function} props.deleteSpace - Function to delete a space
 * @param {Function} props.handleCommandClick - Function to handle command click for multi-selection
 * @param {Function} props.calculateTotalArea - Function to calculate total area
 * @param {Function} props.calculateSensorCounts - Function to calculate sensor counts
 * @param {Object} props.typeVisibility - Object with visibility state for each sensor type
 * @param {Function} props.toggleTypeVisibility - Function to toggle visibility for a specific type
 * @param {Function} props.toggleSelectedSpacesVisibility - Function to toggle visibility for selected spaces
 * @param {Function} props.updateSpaceAttribute - Function to update a space attribute
 * @param {number} props.pixelsPerFoot - Scale in pixels per foot for area calculations
 * @returns {JSX.Element} SpaceTable component
 */
const SpaceTable = ({
  boxes,
  shapes,
  entryPoints,
  selectedSpace,
  selectedSpaces,
  isMultiSelecting,
  activeMode,
  setSelectedSpace,
  setSelectedSpaces,
  setIsMultiSelecting,
  toggleSpaceType,
  updateSpaceCount,
  toggleSpaceVisibility,
  deleteSpace,
  handleCommandClick,
  calculateTotalArea,
  calculateSensorCounts,
  typeVisibility,
  toggleTypeVisibility,
  toggleSelectedSpacesVisibility,
  updateSpaceAttribute,
  pixelsPerFoot
}) => {
  // Prepare data for the table
  const tableData = [
    // Box data
    ...boxes.map(box => {
      // Calculate dimensions and area for display
      const width = box.width || Math.abs((box.end?.x - box.start?.x)) || 0;
      const height = box.height || Math.abs((box.end?.y - box.start?.y)) || 0;
      const widthFt = width / (pixelsPerFoot || 1);
      const heightFt = height / (pixelsPerFoot || 1);
      
      return {
        id: `box-${box.id}`,
        displayId: `Box ${box.id}`,
        type: 'Box',
        area: (
          <div className="area-with-details">
            {formatArea(box.area)}
            <span className="area-calculation">
              {widthFt.toFixed(2)}ft × {heightFt.toFixed(2)}ft
            </span>
          </div>
        ),
        rawArea: box.area,
        sensorType: box.type,
        sensorTypeValue: box.type,
        count: box.count || 1,
        visible: box.visible !== false,
        spaceType: 'box',
        spaceId: box.id,
        spaceCategory: box.spaceCategory || SPACE_TYPES.DESK,
        originalData: box
      };
    }),
    
    // Shape data
    ...shapes.map(shape => ({
      id: `shape-${shape.id}`,
      displayId: `Shape ${shape.id}`,
      type: 'Shape',
      area: (
        <div className="area-with-details">
          {formatArea(shape.area)}
          <span className="area-calculation">
            (polygon with {shape.vertices?.length || 0} points)
          </span>
        </div>
      ),
      rawArea: shape.area,
      sensorType: shape.type,
      sensorTypeValue: shape.type,
      count: shape.count || 1,
      visible: shape.visible !== false,
      spaceType: 'shape',
      spaceId: shape.id,
      spaceCategory: shape.spaceCategory || SPACE_TYPES.DESK,
      originalData: shape
    })),
    
    // Entry point data
    ...entryPoints.map(entry => ({
      id: `entry-${entry.id}`,
      displayId: `Entry ${entry.id}`,
      type: 'Entry Point',
      area: '-',
      rawArea: 0,
      sensorType: SENSOR_TYPES.ENTRY,
      sensorTypeValue: SENSOR_TYPES.ENTRY,
      count: entry.count || 1,
      visible: entry.visible !== false,
      spaceType: 'entry',
      spaceId: entry.id,
      spaceCategory: SPACE_TYPES.ENTRY,
      originalData: entry
    }))
  ];

  // Function to update space category
  const updateSpaceCategory = (spaceType, spaceId, newCategory) => {
    if (updateSpaceAttribute) {
      updateSpaceAttribute(spaceType, spaceId, 'spaceCategory', newCategory);
    }
  };

  // Bulk update sensor type for selected spaces
  const bulkUpdateSensorType = (e) => {
    if (e) e.stopPropagation();
    
    // Process all selected spaces that aren't entry points
    const spacesToUpdate = selectedSpaces.filter(space => space.type !== 'entry');
    
    // Toggle the sensor type for each selected space
    spacesToUpdate.forEach(space => {
      toggleSpaceType(space.type, space.id);
    });
  };

  // Make all selected spaces OA type
  const makeAllOA = (e) => {
    if (e) e.stopPropagation();
    
    // Process all selected spaces that aren't entry points
    const spacesToUpdate = selectedSpaces.filter(space => space.type !== 'entry');
    
    // Find the corresponding table data items
    const itemsToUpdate = spacesToUpdate.map(space => {
      return tableData.find(item => 
        item.spaceType === space.type && item.spaceId === space.id
      );
    }).filter(item => item); // Remove any undefined items
    
    // Make all Waffle spaces into OA
    itemsToUpdate.forEach(item => {
      if (item.sensorType === SENSOR_TYPES.WAFFLE) {
        toggleSpaceType(item.spaceType, item.spaceId);
      }
    });
  };

  // Make all selected spaces Waffle type
  const makeAllWaffle = (e) => {
    if (e) e.stopPropagation();
    
    // Process all selected spaces that aren't entry points
    const spacesToUpdate = selectedSpaces.filter(space => space.type !== 'entry');
    
    // Find the corresponding table data items
    const itemsToUpdate = spacesToUpdate.map(space => {
      return tableData.find(item => 
        item.spaceType === space.type && item.spaceId === space.id
      );
    }).filter(item => item); // Remove any undefined items
    
    // Make all OA spaces into Waffle
    itemsToUpdate.forEach(item => {
      if (item.sensorType === SENSOR_TYPES.OA) {
        toggleSpaceType(item.spaceType, item.spaceId);
      }
    });
  };

  // Get space type icon based on category
  const getSpaceTypeIcon = (category) => {
    switch (category) {
      case SPACE_TYPES.DESK:
        return <img src="/images/slides/desk.png" alt="Desk" className="space-type-icon" />;
      case SPACE_TYPES.ROOM:
        return <img src="/images/slides/meeting.png" alt="Room" className="space-type-icon" />;
      case SPACE_TYPES.FLEX:
        return <img src="/images/slides/flex.png" alt="Flex" className="space-type-icon" />;
      case SPACE_TYPES.PHONE:
        return <img src="/images/slides/phonebooth.png" alt="Phone Booth" className="space-type-icon" />;
      default:
        return <img src="/images/slides/desk.png" alt="Desk" className="space-type-icon" />;
    }
  };

  // Custom styles for color indicators
  const colorIndicatorStyle = (spaceType) => ({
    width: '4px',
    height: '20px',
    backgroundColor: SPACE_TYPE_COLORS[spaceType],
    position: 'absolute',
    left: '0',
    top: '50%',
    transform: 'translateY(-50%)',
    borderRadius: '2px'
  });

  // Define columns for the table
  const columns = [
    {
      id: 'id',
      label: 'ID',
      accessor: 'displayId',
      sortable: true
    },
    {
      id: 'type',
      label: 'Shape',
      accessor: (item) => {
        if (item.spaceType === 'entry') {
          return 'Entry Point';
        }
        
        // Get human-readable name for space category
        const getCategoryName = (category) => {
          switch(category) {
            case SPACE_TYPES.DESK: return 'Desk';
            case SPACE_TYPES.ROOM: return 'Room';
            case SPACE_TYPES.PHONE: return 'Phone Booth';
            case SPACE_TYPES.FLEX: return 'Flex';
            default: return 'Box';
          }
        };
        
        return getCategoryName(item.spaceCategory);
      },
      sortable: true
    },
    {
      id: 'spaceCategory',
      label: 'Type',
      accessor: (item) => {
        if (item.spaceType === 'entry') {
          return <span></span>; // Don't show anything for entry points
        }
        
        return (
          <div className="space-type-selector">
            <div className="space-type-item">
              <button 
                className={`space-type-button ${item.spaceCategory === SPACE_TYPES.ROOM ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCategory(item.spaceType, item.spaceId, SPACE_TYPES.ROOM);
                }}
                title="Room"
                style={item.spaceCategory === SPACE_TYPES.ROOM ? {borderColor: SPACE_TYPE_COLORS[SPACE_TYPES.ROOM]} : {}}
              >
                {getSpaceTypeIcon(SPACE_TYPES.ROOM)}
              </button>
              <div className="space-type-label">Room</div>
            </div>
            <div className="space-type-item">
              <button 
                className={`space-type-button ${item.spaceCategory === SPACE_TYPES.DESK ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCategory(item.spaceType, item.spaceId, SPACE_TYPES.DESK);
                }}
                title="Desk"
                style={item.spaceCategory === SPACE_TYPES.DESK ? {borderColor: SPACE_TYPE_COLORS[SPACE_TYPES.DESK]} : {}}
              >
                {getSpaceTypeIcon(SPACE_TYPES.DESK)}
              </button>
              <div className="space-type-label">Desk</div>
            </div>
            <div className="space-type-item">
              <button 
                className={`space-type-button ${item.spaceCategory === SPACE_TYPES.FLEX ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCategory(item.spaceType, item.spaceId, SPACE_TYPES.FLEX);
                }}
                title="Flex"
                style={item.spaceCategory === SPACE_TYPES.FLEX ? {borderColor: SPACE_TYPE_COLORS[SPACE_TYPES.FLEX]} : {}}
              >
                {getSpaceTypeIcon(SPACE_TYPES.FLEX)}
              </button>
              <div className="space-type-label">Flex</div>
            </div>
            <div className="space-type-item">
              <button 
                className={`space-type-button ${item.spaceCategory === SPACE_TYPES.PHONE ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCategory(item.spaceType, item.spaceId, SPACE_TYPES.PHONE);
                }}
                title="Phone Booth"
                style={item.spaceCategory === SPACE_TYPES.PHONE ? {borderColor: SPACE_TYPE_COLORS[SPACE_TYPES.PHONE]} : {}}
              >
                {getSpaceTypeIcon(SPACE_TYPES.PHONE)}
              </button>
              <div className="space-type-label">Phone</div>
            </div>
          </div>
        );
      },
      sortable: true
    },
    {
      id: 'area',
      label: 'Area (sqft)',
      accessor: (item) => {
        // Handle empty or invalid values
        if (item.rawArea === undefined || item.rawArea === null || isNaN(item.rawArea)) {
          return '0 sqft';
        }
        
        if (item.area && React.isValidElement(item.area)) {
          // If area is already a React element (from our mapping above), just return it
          return item.area;
        }
        
        // Calculate square feet (rawArea is now in square feet)
        const rawAreaInSqFt = Math.max(0, item.rawArea); // Ensure positive
        const roundedSqFt = Math.ceil(rawAreaInSqFt);
        
        // Display the area with calculation details
        return (
          <span className="area-with-details">
            {roundedSqFt.toLocaleString()} sqft
            <span className="area-calculation">
              ({rawAreaInSqFt.toFixed(2)} sqft)
            </span>
          </span>
        );
      },
      sortAccessor: 'rawArea',
      sortable: true
    },
    {
      id: 'sensorType',
      label: 'Sensor',
      accessor: (item) => {
        // For entry points, just show text instead of a dropdown
        if (item.spaceType === 'entry') {
          return <span>{SENSOR_TYPES.ENTRY}</span>;
        }
        
        // For other types, keep the dropdown
        return (
          <select 
            value={item.sensorType} 
            onChange={(e) => {
              e.stopPropagation();
              toggleSpaceType(item.spaceType, item.spaceId);
            }}
          >
            <option value={SENSOR_TYPES.OA}>{SENSOR_TYPES.OA}</option>
            <option value={SENSOR_TYPES.WAFFLE}>{SENSOR_TYPES.WAFFLE}</option>
          </select>
        );
      },
      sortAccessor: 'sensorTypeValue',
      sortable: true
    },
    // Always show count column
    {
      id: 'count',
      label: 'Unit(s)',
      accessor: (item) => {
        if (item.sensorType === SENSOR_TYPES.OA || item.sensorType === SENSOR_TYPES.ENTRY) {
          // For OA and Entry types, show the +/- controls
          return (
            <div className="count-control">
              <span className="count-value">{item.count}</span>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCount(item.spaceType, item.spaceId, item.count - 1);
                }}
                disabled={item.count <= 1}
              >
                -
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  updateSpaceCount(item.spaceType, item.spaceId, item.count + 1);
                }}
              >
                +
              </button>
            </div>
          );
        } else if (item.sensorType === SENSOR_TYPES.WAFFLE) {
          // For Waffle type, use the InPlaceNumberEditor
          return (
            <InPlaceNumberEditor
              value={item.count}
              onSave={(newCount) => {
                updateSpaceCount(item.spaceType, item.spaceId, newCount);
              }}
              className="waffle-count-editor"
            />
          );
        }
        return <span>-</span>;
      },
      sortable: false
    }
  ];

  // Add visibility column
  columns.push({
    id: 'visible',
    label: 'Show',
    accessor: (item) => (
      <button 
        className={`visibility-toggle ${item.visible ? 'visible' : 'hidden'}`}
        onClick={(e) => {
          e.stopPropagation();
          toggleSpaceVisibility(item.spaceType, item.spaceId);
        }}
        title={item.visible ? "Hide this space" : "Show this space"}
      >
        {item.visible ? '👁️' : '👁️‍🗨️'}
      </button>
    ),
    sortable: false
  });

  // Add actions column
  columns.push({
    id: 'actions',
    label: 'Delete',
    accessor: (item) => (
      <DeleteSpaceButton 
        spaceType={item.spaceType} 
        spaceId={item.spaceId} 
        onDelete={deleteSpace} 
      />
    ),
    sortable: false
  });

  // Custom row renderer to handle selection and hover
  const renderRow = (item, index, onRowHover, onRowLeave) => {
    const isSelected = selectedSpace && selectedSpace.type === item.spaceType && selectedSpace.id === item.spaceId;
    const isMultiSelected = selectedSpaces.some(space => space.type === item.spaceType && space.id === item.spaceId);
    
    return (
      <tr 
        key={`${item.id}-${index}`}
        className={`${isSelected || isMultiSelected ? 'selected-space' : ''} ${!item.visible ? 'hidden-space' : ''}`}
        onMouseEnter={() => onRowHover && onRowHover({ type: item.spaceType, id: item.spaceId, isHighlightOnly: true })}
        onMouseLeave={() => onRowLeave && onRowLeave({ isHighlightOnly: true })}
        onClick={(e) => {
          e.stopPropagation();
          console.log(`[TABLE ROW CLICK] Space type: ${item.spaceType}, ID: ${item.spaceId}`);
          
          // Only allow selection when in SELECT_MODE
          if (activeMode !== DRAWING_MODES.SELECT_MODE) {
            console.log(`Not in SELECT_MODE, ignoring selection`);
            return;
          }
          
          if (e.metaKey || e.ctrlKey) {
            // Handle command click for multi-selection
            handleCommandClick && handleCommandClick(item.spaceType, item.spaceId);
          } else {
            // Handle single click selection
            setSelectedSpace({ type: item.spaceType, id: item.spaceId });
            setSelectedSpaces([]);
            setIsMultiSelecting(false);
          }
        }}
      >
        {columns.map(column => (
          <td key={column.id}>
            {typeof column.accessor === 'function' 
              ? column.accessor(item) 
              : item[column.accessor]}
          </td>
        ))}
      </tr>
    );
  };

  // Create summary row
  const summaryRow = (boxes.length > 0 || shapes.length > 0 || entryPoints.length > 0) ? (
    <tr className="summary-row">
      <td colSpan="2"><strong>Total</strong></td>
      <td></td>
      <td>
        {/* Format the total area with calculation details */}
        {(() => {
          const totalAreaSqFt = calculateTotalArea();
          const roundedSqFt = Math.ceil(totalAreaSqFt);
          
          return (
            <strong className="area-with-details">
              {roundedSqFt.toLocaleString()} sqft
              <span className="area-calculation">
                ({totalAreaSqFt.toFixed(2)} sqft)
              </span>
            </strong>
          );
        })()}
      </td>
      <td colSpan={2}>
        <div className="sensor-summary">
          <span>OA: {calculateSensorCounts().oaCount.toLocaleString()}</span>
          <span>Waffle: {calculateSensorCounts().waffleCount.toLocaleString()}</span>
          <span>Entry: {calculateSensorCounts().entryCount.toLocaleString()}</span>
        </div>
      </td>
    </tr>
  ) : null;

  return (
    <div className="space-table-container">
      {/* Type visibility toggles - REMOVED and moved to Legend */}
      
      {selectedSpaces.length > 0 && (
        <div className="multi-selection-controls">
          <span>{selectedSpaces.length} spaces selected</span>
          <button 
            className="action-button show-button" 
            onClick={(e) => {
              e && e.stopPropagation();
              toggleSelectedSpacesVisibility(true);
            }}
          >
            Show Selected
          </button>
          <button 
            className="action-button hide-button" 
            onClick={(e) => {
              e && e.stopPropagation();
              toggleSelectedSpacesVisibility(false);
            }}
          >
            Hide Selected
          </button>
          
          {/* New bulk sensor type controls */}
          <div className="bulk-sensor-controls">
            <span>Set sensors:</span>
            <button 
              className="action-button sensor-toggle-button" 
              onClick={bulkUpdateSensorType}
              title="Toggle between OA and Waffle sensor types for all selected spaces"
            >
              Toggle All
            </button>
            <button 
              className="action-button grey-button" 
              onClick={makeAllOA}
              title="Convert all selected spaces to OA sensor type"
            >
              All OA
            </button>
            <button 
              className="action-button grey-button" 
              onClick={makeAllWaffle}
              title="Convert all selected spaces to Waffle sensor type"
            >
              All Waffle
            </button>
          </div>
        </div>
      )}
      
      <SortableTable 
        data={tableData}
        columns={columns}
        onRowHover={(item) => {
          // If highlighting only, don't change selection state
          if (item.isHighlightOnly) {
            // Optional: add highlight state for future hover effects
            return;
          }
          setSelectedSpace({ type: item.spaceType, id: item.spaceId });
        }}
        onRowLeave={(info) => {
          // If highlighting only, don't change selection state
          if (info && info.isHighlightOnly) {
            // Optional: remove highlight state for future hover effects
            return;
          }
          setSelectedSpace(null);
        }}
        renderRow={renderRow}
        summaryRow={summaryRow}
      />
    </div>
  );
};

export default SpaceTable;